import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import { FORMAS_PAGO, URL_FINANZAS, auth, fmt, redondea, reparte } from "./finanzasUtils";

const URL_ABONOS = process.env.REACT_APP_URL_ABONOS;
const URL_PAGOS = process.env.REACT_APP_URL_ABONOS_GASTOS;

// Alta de abonos del cliente o pagos al broker de un contenedor: el importe liquida de lo más viejo
// a lo más nuevo y cada renglón se puede ajustar.  abierto: { modo: "abonos" | "pagos", contenedor }
export default function ModalAplicar({ abierto, cerrar, onSaved }) {
  const { modo, contenedor } = abierto || {};
  const esAbono = modo === "abonos";
  const [fecha, setFecha] = useState("");
  const [formaPago, setFormaPago] = useState("Transfer");
  const [concepto, setConcepto] = useState("Abono");
  const [observaciones, setObservaciones] = useState("");
  const [importe, setImporte] = useState("");
  const [filas, setFilas] = useState([]);
  const [sinAutorizar, setSinAutorizar] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setFecha(moment().format("YYYY-MM-DD"));
    setFormaPago("Transfer");
    setConcepto("Abono");
    setObservaciones("");
    setImporte("");
    setFilas([]);
    setCargando(true);
    axios
      .get(`${URL_FINANZAS}Contenedor/${contenedor._id}`, auth())
      .then(({ data }) => {
        setFilas((esAbono ? data.porCobrar : data.porPagar).map((f) => ({ ...f, importe: 0 })));
        setSinAutorizar(data.sinAutorizar);
      })
      .catch((err) => Swal.fire({ icon: "error", title: "No se pudo cargar", text: err?.response?.data?.message || err.message }))
      .finally(() => setCargando(false));
  }, [abierto]); // eslint-disable-line

  const aplicado = redondea(filas.reduce((s, f) => s + (Number(f.importe) || 0), 0));
  const porAplicar = redondea(redondea(importe) - aplicado);
  const pendiente = redondea(filas.reduce((s, f) => s + f.saldo, 0));

  const cambiaImporte = (valor) => {
    setImporte(valor);
    setFilas((fs) => reparte(fs, valor));
  };
  const cambiaFila = (id, valor) => {
    const fila = filas.find((f) => f.id === id);
    if (Number(valor) < 0) return;
    if (Number(valor) > fila.saldo) {
      Swal.fire({ icon: "error", title: "Excede el saldo", text: `El saldo de este renglón es ${fmt(fila.saldo)}` });
      return;
    }
    setFilas(filas.map((f) => (f.id === id ? { ...f, importe: valor } : f)));
  };

  const payload = (f) => {
    const notas = observaciones.trim() || "NA";
    if (esAbono) {
      return {
        fecha, importe: Number(f.importe), clientes: contenedor.idClientes, formaPago, concepto, observaciones: notas, contenedores: contenedor._id,
        ...(f.clase === "cargo" ? { cargosContenedor: f.id } : { surtidos: f.id }),
      };
    }
    return { fecha, importe: Number(f.importe), brokers: f.broker, gastosContenedor: f.id, formaPago, observaciones: notas, idContenedor: contenedor._id };
  };

  const guardar = async () => {
    if (!fecha || !(aplicado > 0) || porAplicar !== 0) {
      Swal.fire({ icon: "warning", title: "Revisa los importes", text: porAplicar !== 0 ? `Falta aplicar ${fmt(porAplicar)}` : "Captura el importe" });
      return;
    }
    setGuardando(true);
    let guardados = 0;
    const url = esAbono ? URL_ABONOS : URL_PAGOS;
    const aplicar = filas.filter((f) => Number(f.importe) > 0);
    try {
      for (const f of aplicar) {
        await axios.post(url, payload(f), auth());
        guardados += 1;
      }
      Swal.fire({ icon: "success", title: esAbono ? "Abono registrado" : "Pago registrado", timer: 1300, showConfirmButton: false });
      cerrar();
    } catch (err) {
      // Lo que ya entró bajó el saldo: se recarga para no aplicarlo dos veces
      Swal.fire({ icon: "error", title: "No se pudo guardar", text: `Se guardaron ${guardados} de ${aplicar.length}. ${err?.response?.data?.message || err.message}` });
      if (guardados) cerrar();
    } finally {
      setGuardando(false);
      if (guardados) onSaved();
    }
  };

  return (
    <Modal size="xl" isOpen={!!abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>
        <span>{esAbono ? "Abono del cliente" : "Pago al broker"} · Contenedor {contenedor ? contenedor.referencia : ""}</span>
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md={3} className="mb-2">
            <Label>Fecha</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </Col>
          <Col md={3} className="mb-2">
            <Label>Forma de pago</Label>
            <Input type="select" value={formaPago} onChange={(e) => setFormaPago(e.target.value)}>
              {FORMAS_PAGO.map((x) => <option key={x}>{x}</option>)}
            </Input>
          </Col>
          {esAbono ? (
            <Col md={2} className="mb-2">
              <Label>Concepto</Label>
              <Input type="select" value={concepto} onChange={(e) => setConcepto(e.target.value)}>
                <option value="Abono">Pago</option>
                <option value="Bonificacion">Bonificación</option>
              </Input>
            </Col>
          ) : null}
          <Col md={esAbono ? 4 : 6} className="mb-2">
            <Label>Observaciones</Label>
            <Input type="text" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </Col>
          <Col md={3} className="mb-2">
            <Label>Importe por aplicar</Label>
            <Input type="number" min="0" step="0.01" value={importe} onChange={(e) => cambiaImporte(e.target.value)} />
          </Col>
          <Col md={3} className="mb-2">
            <Label>Saldo por aplicar</Label>
            <Input type="text" value={fmt(porAplicar)} disabled />
          </Col>
        </Row>

        {cargando ? (
          <div className="rc-state"><i className="fas fa-spinner fa-spin" /><div>Cargando pendientes…</div></div>
        ) : filas.length === 0 ? (
          <div className="rc-state">
            <i className="fas fa-check-circle" />
            <div>
              {esAbono
                ? "Este contenedor no tiene saldo por cobrar."
                : sinAutorizar
                  ? `Hay ${sinAutorizar} gastos sin autorizar: autorízalos en el detalle para poder pagarlos.`
                  : "Este contenedor no tiene gastos por pagar."}
            </div>
          </div>
        ) : (
          <Table size="sm">
            <thead>
              <tr>
                <th>Fecha</th><th>Movimiento</th><th className="td-num">Total</th><th className="td-num">Saldo</th><th>Importe</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.id}>
                  <td>{f.fecha}</td>
                  <td>{f.movimiento}</td>
                  <td className="td-num td-money">{fmt(f.total)}</td>
                  <td className="td-num td-money">{fmt(f.saldo)}</td>
                  <td style={{ width: 170 }}>
                    <Input bsSize="sm" type="number" min="0" step="0.01" value={f.importe} onChange={(e) => cambiaFila(f.id, e.target.value)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter>
        <strong style={{ marginRight: "auto" }}>Pendiente {fmt(pendiente)} · Aplicado {fmt(aplicado)}</strong>
        <Button color="secondary" onClick={cerrar}><i className="fas fa-times" /> Cerrar</Button>
        <Button color="success" onClick={guardar} disabled={guardando || !filas.length}><i className="fas fa-save" /> Guardar</Button>
      </ModalFooter>
    </Modal>
  );
}
